import './prosentvis-bilde.module.css'
import { testReducedMotion, toArray, closest } from '@nrk/dh-basis-common'
import { calculateValue } from './easing/interpolation'
import { LayerAnimation, PropertyType } from '../../types/prosentvis-bilde'

export default class ProsentvisBilde extends HTMLElement {
  private _reducedMotion = false

  static get observedAttributes() {
    return ['data-percent']
  }

  connectedCallback() {
    this.#updateSizes()
    this._reducedMotion = testReducedMotion()
    this._prosentChanged()
  }

  attributeChangedCallback(name, prevValue, newValue) {
    const valueChanged = prevValue !== newValue
    if (!valueChanged) {
      return
    }

    if (name === 'data-percent') {
      this._prosentChanged()
    }
  }

  #isFullWidth() {
    return (
      closest(this, 'article')?.classList?.contains('article-feature') &&
      closest(this, '.remoterenderedcontent-reference')?.classList.contains('fc')
    )
  }

  #updateSizes() {
    if (this.#isFullWidth()) {
      this.keyframedElements.forEach((elm) => elm.setAttribute('sizes', '100vw'))
    }
  }

  _prosentChanged() {
    const keyframedElements = this.keyframedElements
    const percent = this.percent * 100

    if (this._reducedMotion && percent !== 0) {
      return
    }

    keyframedElements.forEach((elm) => {
      const animation: LayerAnimation = JSON.parse(elm.getAttribute('data-layer-animation'))

      const scale = getScaleTransform(animation, percent)
      const rotate = getRotateTransform(animation, percent)
      const translateX = getTranslateTransform('translateX', animation, percent)
      const translateY = getTranslateTransform('translateY', animation, percent)
      elm.style.transform = [translateX, translateY, rotate, scale].filter(Boolean).join(' ')

      const opacity = getAnimationValue('opacity', animation, percent)
      if (isNumber(opacity)) {
        elm.style.opacity = opacity
      }
    })
  }

  get keyframedElements() {
    return toArray(this.querySelectorAll('[data-layer-animation]'))
  }

  get percent() {
    return parseFloat(this.getAttribute('data-percent') || '0.0')
  }

  set percent(val) {
    this.setAttribute('data-percent', val.toFixed(2))
  }
}

function getAnimationValue(propType: PropertyType, animation: LayerAnimation, percentage: number) {
  const property = animation.find((p) => p.type === propType)
  return property ? calculateValue(property.keyframes, percentage) : undefined
}

function getRotateTransform(animation: LayerAnimation, percentage: number) {
  const rotate = getAnimationValue('rotate', animation, percentage)
  return isNumber(rotate) ? `rotate(${rotate}deg)` : ''
}

function getScaleTransform(animation: LayerAnimation, percentage: number) {
  const scale = getAnimationValue('scale', animation, percentage)
  return isNumber(scale) ? `scale(${scale})` : ''
}

function getTranslateTransform(
  prop: 'translateX' | 'translateY',
  animation: LayerAnimation,
  percentage: number
) {
  const translate = getAnimationValue(prop, animation, percentage)
  return isNumber(translate) ? `${prop}(${translate}%)` : ''
}

function isNumber(n: unknown): n is number {
  return typeof n === 'number'
}
